import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { EduContent, EduContentTypeEnum, FavoriteInterface, ViewListAsEnum } from '@campus/dal';
import { EduContentSearchResultInterface } from '@campus/shared';
import { DcrSection, DcrSectionInterface } from '@diekeure/dcr-components';
import '@diekeure/dcr-components/dcr-section.js';
import { Dictionary } from '@ngrx/entity';
import { Category } from '../../interfaces';

@Component({
  standalone: false,
  // tslint:disable-next-line
  selector: 'campus-toc-result-list-item-groups',
  templateUrl: './toc-result-list-item-groups.component.html',
  styleUrls: ['./toc-result-list-item-groups.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TocResultListItemGroupsComponent implements OnInit, AfterViewInit {
  private _cdRef = inject(ChangeDetectorRef);

  @HostBinding('attr.data-cy')
  dataCy = 'toc-result-list-item-groups';
  @HostBinding('class.toc-result-list-item-groups')
  tocResultListItemGroupsClass = true;
  @HostBinding('class.toc-result-list-item-groups--selected')
  @Input()
  selected = false;
  @Input() get data() {
    return this._data;
  }
  set data(value) {
    if (!value) return;
    this._data = value;

    if (!this._dataInitialised) {
      this._dataInitialised = true;
      this.applyAllExpanded(this._allExpanded, false);
      return;
    }

    // Subsequent emissions (filter changes, selection re-emits) can introduce new categories
    // or drop existing ones. Seed new ids from the toolbar's last command so the toggle stays
    // authoritative for newcomers, while preserving per-section state for ids the user
    // already sees on screen.
    const currentIds = this.getProductTypeIds();
    const next: Record<number, boolean> = {};
    for (const id of currentIds) {
      if (id in this.activePanels) next[id] = this.activePanels[id];
      else if (this._lastCommand) next[id] = true;
    }
    this.activePanels = next;
    this._cdRef.markForCheck();
  }

  @Input() favoriteEduContents: Dictionary<FavoriteInterface[]>;

  @Input() viewType?: ViewListAsEnum;
  @Input() set allExpanded(value: boolean | null | undefined) {
    if (value === null || value === undefined) return;
    const next = !!value;
    // External value reflects a toolbar command (or a self-emit echo). Always update
    // `_lastCommand` so new sections on the next data emit honour the most recent intent.
    this._lastCommand = next;
    if (next === this._allExpanded) return;
    this._allExpanded = next;
    // After the view is initialised, propagate external changes via animated apply.
    // Before init, sectionRefs aren't populated yet — the silent ngOnInit path covers initial state.
    if (this._viewInitialized) {
      this.applyAllExpanded(this._allExpanded, true);
    }
  }

  public activePanels: Record<number, boolean> = {};

  @ViewChildren('section') private sectionRefs!: QueryList<ElementRef<DcrSectionInterface>>;

  @Output() toggleFavorite = new EventEmitter<EduContent>();
  @Output() selectionChange = new EventEmitter<{ value: EduContentSearchResultInterface; checked: boolean }>();
  @Output() clickLevelAttempts = new EventEmitter<EduContentSearchResultInterface>();
  @Output() allExpandedChange = new EventEmitter<boolean>();

  public EduContentTypeEnum = EduContentTypeEnum;
  public ViewListAsEnum = ViewListAsEnum;

  private _data: Category[];
  private _allExpanded = true;
  // Toolbar's last explicit command — drives seeding for new section ids on data re-emits.
  // Stays decoupled from `_allExpanded`, which tracks "any section open" so per-section
  // toggles don't override the toolbar's intent for newly arrived ids.
  private _lastCommand = true;
  private _dataInitialised = false;
  private _viewInitialized = false;

  constructor() {}

  ngOnInit() {
    this.applyAllExpanded(this._allExpanded, false);
  }

  ngAfterViewInit() {
    this._viewInitialized = true;
  }

  public trackById(index: number, { id }: { id: number }) {
    return id;
  }

  public trackByEduContentId(index: number, result: EduContentSearchResultInterface) {
    return result.eduContent.id;
  }

  public onEduContentSelectionChange({ value, checked }: { value: EduContentSearchResultInterface; checked: boolean }) {
    this.selectionChange.emit({ value, checked });
  }

  public onToggleFavorite(eduContent: EduContent) {
    this.toggleFavorite.emit(eduContent);
  }

  /** Public imperative API — open all sections. Pass animate=false for silent state syncs. */
  public expandAll(animate = true): void {
    this.setAllExpanded(true, animate);
  }

  /** Public imperative API — close all sections. Pass animate=false for silent state syncs. */
  public collapseAll(animate = true): void {
    this.setAllExpanded(false, animate);
  }

  private setAllExpanded(value: boolean, animate: boolean): void {
    if (value === this._allExpanded) {
      if (!animate) this.applyAllExpanded(value, false);
      return;
    }
    this._allExpanded = value;
    this.applyAllExpanded(value, animate);
  }

  public addActivePanel(id: number) {
    this.activePanels[id] = true;
    // Toolbar reflects "any section open": the next toolbar click should collapse everything.
    this.emitExpandState(true);
    this._cdRef.markForCheck();
  }

  public removeActivePanel(id: number) {
    delete this.activePanels[id];
    this.emitExpandState(Object.keys(this.activePanels).length > 0);
    this._cdRef.markForCheck();
  }

  private applyAllExpanded(value: boolean, animate: boolean): void {
    if (!animate) {
      // Silent path: drive [open] via activePanels. CSS toggles content visibility instantly.
      if (value) {
        const keys = this.getProductTypeIds();
        this.activePanels = keys.reduce((acc, key) => ({ ...acc, [key]: true }), {});
      } else {
        this.activePanels = {};
      }
      this._cdRef.markForCheck();
      return;
    }
    // Animated path: dispatch expand()/collapse() FIRST so dcr-section enters its
    // `.animating` state before Angular re-evaluates `[open]`. Updating activePanels
    // beforehand would let the [open] binding flip the section to its target visual
    // state via CSS before the animation class is on, causing a one-frame flash.
    this.sectionRefs?.forEach((ref) => {
      const el = ref.nativeElement as DcrSection;
      if (value) el.expand?.();
      else el.collapse?.();
    });
    // Then mirror toolbar intent in activePanels so the component's state stays in
    // sync with the command even if `opened`/`closed` events never fire (tests, edge
    // cases). Sections that already started animating ignore the [open] flip.
    if (value) {
      const keys = this.getProductTypeIds();
      this.activePanels = keys.reduce((acc, key) => ({ ...acc, [key]: true }), {});
    } else {
      this.activePanels = {};
    }
    this._cdRef.markForCheck();
  }

  private emitExpandState(value: boolean): void {
    if (this._allExpanded !== value) {
      this._allExpanded = value;
      this.allExpandedChange.emit(value);
    }
  }

  private getProductTypeIds(): number[] {
    return this.data?.map((category) => category.id) || [];
  }

  onClickLevelAttempts(event: EduContentSearchResultInterface) {
    this.clickLevelAttempts.emit(event);
  }
}
